import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { Container, Section } from 'chaoskit/src/components'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import { useState, useEffect } from 'react'
import { graphql } from 'gatsby'
import { navigate } from '@reach/router'

import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import {
  createUrl,
  urlToSearchState,
  searchStateToUrl,
} from '~partials/search/utility'
import {
  Hit,
  CustomCategoryMenu,
  CustomAuthorMenu,
} from '~partials/search/algolia-blog'
import {
  CustomInfiniteHits,
  CustomSearchBox,
  CustomClearRefinements,
  DEBOUNCE_TIME,
} from '~partials/search/algolia-global'
import useAlgoliaSearchClient from '~hooks/useAlgoliaSearchClient'
import PostHero from '~partials/blog/PostHero'
import useSiteMetadata from '~hooks/useSiteMetadata'

const Blog = ({
  data: {
    latest: {
      edges: [{ node: heroNode }],
    },
  },
  location,
}) => {
  const theme = useTheme()

  const { siteUrl } = useSiteMetadata()

  const searchClient = useAlgoliaSearchClient((state) => state.searchClient)
  const [searchState, setSearchState] = useState(urlToSearchState(location))
  const [debouncedSetState, setDebouncedSetState] = useState(null)

  const handleSearchStateChange = (updatedSearchState) => {
    clearTimeout(debouncedSetState)

    setDebouncedSetState(
      setTimeout(() => {
        navigate(searchStateToUrl(updatedSearchState), updatedSearchState)
      }, DEBOUNCE_TIME)
    )

    setSearchState(updatedSearchState)
  }

  useEffect(() => {
    setSearchState(urlToSearchState(location))
  }, [location])

  // Workaround for Gatsby's location prop not reliably being available on server-loads
  const heroNodeCurrentPageUrl = `${siteUrl}${heroNode.fields.fullUrl}`

  return (
    <BaseLayout
      headerProps={{ hasDefaultOverlay: true }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO title="Breaking Things on Purpose | Gremlin Blog" />
      <InstantSearch
        indexName="resources"
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={handleSearchStateChange}
        createURL={createUrl}
      >
        <Configure filters="articleLabel:'Blog Posts'" hitsPerPage="6" />
        <PostHero
          currentPageUrl={heroNodeCurrentPageUrl}
          date={heroNode.frontmatter.date}
          excerpt={heroNode.excerpt}
          featuredImage={heroNode.fields.featuredImage}
          timeToRead={heroNode.timeToRead}
          title={heroNode.frontmatter.title}
          fullUrl={heroNode.fields.fullUrl}
          hasLink
          hasExcerpt
          isHero
        />

        <Section>
          <Container>
            <div
              css={{
                [theme.mq.medium]: {
                  display: 'grid',
                  gridAutoColumns: '1fr',
                  gridAutoFlow: 'column',
                  alignItems: 'center',
                  gap: theme.space.base,
                },
              }}
            >
              <div>
                <CustomSearchBox
                  label="Search posts"
                  placeholderText="Chaos Engineering"
                />
              </div>
              <div
                css={{
                  [theme.mq.small__max]: {
                    display: 'none',
                  },

                  [theme.mq.medium]: {
                    gridColumn: 'span 2',
                    justifySelf: 'end',
                  },
                }}
              >
                <CustomCategoryMenu attribute="categories" limit={4} />
              </div>
            </div>
            <CustomAuthorMenu attribute="author" />
            <CustomClearRefinements />
            <CustomInfiniteHits
              hitTemplate={Hit}
              customCss={{
                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(3, 1fr)',
                },
              }}
            />
          </Container>
        </Section>
      </InstantSearch>
    </BaseLayout>
  )
}

Blog.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export default Blog

export const pageQuery = graphql`
  query BlogPageData {
    latest: allMdx(
      limit: 1
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        fileAbsolutePath: { regex: "/src/posts/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...PostTeaserFragment
        }
      }
    }
  }
`
